import { httpClient } from '@/shared/api/httpClient';
import type { FormaPagamento } from '@/features/financeiro/api/financeiroApi';
import { ehErroProdutoBloqueado } from '@/features/complementos/api/complementosApi';
import { avisarProdutoBloqueado } from '@/features/complementos/avisoBloqueio';

export type TipoDescontoItem = 'Percentual' | 'Valor';

export type CaixaResumo = {
  id: string;
  colaboradorId: string;
  colaboradorNome: string | null;
  abertoEm: string;
  valorAbertura: number;
  fechadoEm: string | null;
  valorEsperado: number | null;
  valorInformado: number | null;
  diferenca: number | null;
  justificativa: string | null;
  /**
   * true quando o caixa ficou aberto de um dia para o outro e fechou
   * sozinho pelo valor esperado. Ninguém contou a gaveta.
   */
  fechadoSemConferencia: boolean;
  lancamentoId: string | null;
  quantidadeVendas: number;
  totalVendas: number;
  aberto: boolean;
  syncPendente: boolean;
};

export type TotalPorForma = { formaPagamento: string; valor: number };

export type TipoMovimentoCaixa = 'Sangria' | 'Suprimento' | 'Recebimento';

export type MovimentoCaixa = {
  id: string;
  tipo: TipoMovimentoCaixa;
  valor: number;
  motivo: string;
  /** Em `Recebimento`, o lançamento a receber que foi baixado. */
  lancamentoId: string | null;
  criadoEm: string;
};

export type FiadoEmAberto = {
  lancamentoId: string;
  vendaNumero: string | null;
  clienteId: string | null;
  clienteNome: string;
  valor: number;
  valorPago: number;
  saldo: number;
  vencimento: string;
  diasAtraso: number;
};

/**
 * Duas contas que divergem de propósito.
 *
 * A gaveta é dinheiro físico e inclui a entrada de uma venda fiada. O
 * consolidado é a receita que vai ao financeiro e exclui o fiado, que já
 * tem lançamento próprio.
 */
export type ResumoCaixa = {
  caixaId: string;
  valorAbertura: number;
  quantidadeVendas: number;
  totalVendas: number;
  totalFiado: number;
  /** O que vira lançamento no financeiro. */
  totalConsolidado: number;
  totaisPorForma: TotalPorForma[];
  /** Só o que entrou em dinheiro — cartão e Pix não passam pela gaveta. */
  totalDinheiro: number;
  suprimentos: number;
  sangrias: number;
  /**
   * Fiado que o cliente voltou para pagar em dinheiro. Entra na gaveta,
   * nunca no consolidado — a receita já foi registrada no lançamento a
   * receber quando a venda aconteceu.
   */
  recebimentos: number;
  movimentos: MovimentoCaixa[];
  esperadoGaveta: number;
};

export type ItemVendaDetalhe = {
  id: string;
  /** Em devolução, o item da venda original que voltou. */
  itemOrigemId: string | null;
  /** Quanto desta linha já foi devolvido (só na venda original). */
  quantidadeDevolvida: number;
  produtoId: string;
  produtoCodigo: string | null;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  descontoTipo: TipoDescontoItem | null;
  descontoValor: number | null;
  subtotalBruto: number;
  valorDesconto: number;
  subtotal: number;
  ordem: number;
};

export type PagamentoVendaDetalhe = {
  id: string;
  formaPagamento: string;
  valor: number;
  valorRecebido: number | null;
  troco: number | null;
  ordem: number;
};

export type VendaResumo = {
  id: string;
  numero: string;
  numeroProvisorio: string;
  /** Preenchido quando este registro é a devolução de outra venda. */
  vendaOrigemId: string | null;
  vendaOrigemNumero: string | null;
  fiado: boolean;
  lancamentoId: string | null;
  caixaId: string;
  colaboradorId: string;
  colaboradorNome: string | null;
  clienteId: string | null;
  clienteNome: string;
  data: string;
  valorBruto: number;
  valorDesconto: number;
  valorTotal: number;
  status: string;
  observacoes: string | null;
  quantidadeItens: number;
  /** true enquanto o número ainda é o provisório impresso no cupom. */
  syncPendente: boolean;
};

export type VendaDetalhe = VendaResumo & {
  itens: ItemVendaDetalhe[];
  pagamentos: PagamentoVendaDetalhe[];
};

export type ItemVendaEntrada = {
  produtoId?: string | null;
  codigo?: string | null;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  descontoTipo?: TipoDescontoItem | null;
  descontoValor?: number | null;
};

export type PagamentoVendaEntrada = {
  formaPagamento: FormaPagamento | string;
  valor: number;
  valorRecebido?: number | null;
};

export type CriarVendaRequest = {
  /**
   * UUID gerado na tela ANTES de enviar. É a chave de idempotência:
   * clique duplo no balcão ou retentativa depois de um timeout devolvem a
   * mesma venda, sem baixar estoque nem cobrar de novo.
   */
  id: string;
  caixaId: string;
  clienteId?: string | null;
  /**
   * Nome digitado no balcão quando o cliente ainda não existe — o backend
   * cadastra na hora. Parar a venda para abrir o cadastro é a fricção que
   * faz o operador anotar num papel.
   */
  clienteNovo?: { nome: string } | null;
  itens: ItemVendaEntrada[];
  pagamentos: PagamentoVendaEntrada[];
  observacoes?: string | null;
  /**
   * Venda no fiado: exige cliente, aceita entrada parcial (ou nenhuma) e
   * gera um lançamento a receber próprio, fora do consolidado do caixa.
   */
  fiado?: boolean;
  /** Só no fiado. Padrão: 30 dias. */
  vencimento?: string | null;
};

export type ItemDevolucaoEntrada = {
  itemId: string;
  quantidade: number;
};

export type DevolverVendaRequest = {
  id: string;
  /** Caixa aberto de HOJE — devolução nunca reabre caixa fechado. */
  caixaId: string;
  itens: ItemDevolucaoEntrada[];
  formaPagamento: string;
  motivo?: string | null;
};

export type DevolverVendaResposta = { id: string; numero: string; valor: number };

export type MovimentoCaixaRequest = {
  tipo: 'Sangria' | 'Suprimento';
  valor: number;
  motivo: string;
};

export type ReceberFiadoRequest = {
  lancamentoId: string;
  valor: number;
  formaPagamento: string;
};

export type ResumoPdvPorColaborador = {
  colaboradorId: string;
  colaboradorNome: string;
  quantidadeVendas: number;
  total: number;
};

export type ProdutoVendidoPdv = {
  produtoId: string;
  codigo: string | null;
  nome: string;
  quantidade: number;
  total: number;
};

export type DevolucaoPdv = {
  id: string;
  numero: string;
  data: string;
  vendaOrigemNumero: string | null;
  clienteNome: string;
  valor: number;
  motivo: string | null;
};

export type RelatorioPdv = {
  quantidadeVendas: number;
  quantidadeDevolucoes: number;
  totalBruto: number;
  totalDevolvido: number;
  totalLiquido: number;
  totalFiado: number;
  ticketMedio: number;
  porFormaPagamento: TotalPorForma[];
  porColaborador: ResumoPdvPorColaborador[];
  maisVendidos: ProdutoVendidoPdv[];
  devolucoes: DevolucaoPdv[];
  caixas: number;
};

export type CriarVendaResposta = { id: string; numero: string; troco: number };

export type AbrirCaixaRequest = {
  colaboradorId: string;
  pin: string;
  valorAbertura: number;
};

export type FecharCaixaRequest = {
  pin: string;
  valorInformado: number;
  justificativa?: string | null;
};

export type FecharCaixaResposta = { lancamentoId: string | null; diferenca: number };

function querystring(params: Record<string, string | undefined>) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, v);
  }
  const s = usp.toString();
  return s === '' ? '' : `?${s}`;
}

export type FiltrosVendas = {
  caixaId?: string;
  inicio?: string;
  fim?: string;
  busca?: string;
};

/**
 * O PDV é produto add-on: sem ele, caixa, vendas e relatório respondem
 * `pdv.inativo` (ou `pdv.sem_conexao` no desktop offline sem cópia
 * válida). Toda chamada dessas rotas passa por aqui para que a recusa
 * chegue ao gate do PDV — inclusive as que a tela engole com
 * `.catch(() => null)` — e ele troque o conteúdo pela tela de bloqueio.
 */
function avisandoBloqueio<T>(promessa: Promise<T>): Promise<T> {
  return promessa.catch((erro: unknown) => {
    if (ehErroProdutoBloqueado(erro, 'pdv')) avisarProdutoBloqueado('pdv');
    throw erro;
  });
}

const httpPdv = {
  get: <T,>(caminho: string) => avisandoBloqueio(httpClient.get<T>(caminho)),
  post: <T,>(caminho: string, corpo: unknown) => avisandoBloqueio(httpClient.post<T>(caminho, corpo)),
};

export const pdvApi = {
  caixaAberto: (colaboradorId: string) =>
    httpPdv.get<CaixaResumo | null>(`/api/caixa/aberto/${colaboradorId}`),
  obterCaixa: (id: string) => httpPdv.get<CaixaResumo>(`/api/caixa/${id}`),
  resumoCaixa: (id: string) => httpPdv.get<ResumoCaixa>(`/api/caixa/${id}/resumo`),
  listarCaixas: (filtros: { colaboradorId?: string; inicio?: string; fim?: string } = {}) =>
    httpPdv.get<CaixaResumo[]>(`/api/caixa${querystring(filtros)}`),
  abrirCaixa: (cmd: AbrirCaixaRequest) => httpPdv.post<{ id: string }>('/api/caixa/abrir', cmd),
  fecharCaixa: (id: string, cmd: FecharCaixaRequest) =>
    httpPdv.post<FecharCaixaResposta>(`/api/caixa/${id}/fechar`, cmd),
  registrarMovimento: (caixaId: string, cmd: MovimentoCaixaRequest) =>
    httpPdv.post<{ id: string }>(`/api/caixa/${caixaId}/movimento`, cmd),
  fiadoEmAberto: (clienteId?: string) =>
    httpPdv.get<FiadoEmAberto[]>(
      `/api/caixa/fiado/em-aberto${clienteId ? `?clienteId=${encodeURIComponent(clienteId)}` : ''}`,
    ),
  receberFiado: (caixaId: string, cmd: ReceberFiadoRequest) =>
    httpPdv.post<{ movimentoId: string | null; saldo: number }>(
      `/api/caixa/${caixaId}/receber-fiado`,
      cmd,
    ),

  listarVendas: (filtros: FiltrosVendas = {}) =>
    httpPdv.get<VendaResumo[]>(`/api/vendas${querystring(filtros)}`),
  vendasPorProduto: (produtoId: string) =>
    httpPdv.get<VendaResumo[]>(`/api/vendas/por-produto/${produtoId}`),
  obterVenda: (id: string) => httpPdv.get<VendaDetalhe>(`/api/vendas/${id}`),
  criarVenda: (cmd: CriarVendaRequest) =>
    httpPdv.post<CriarVendaResposta>('/api/vendas', cmd),
  devolver: (vendaId: string, cmd: DevolverVendaRequest) =>
    httpPdv.post<DevolverVendaResposta>(`/api/vendas/${vendaId}/devolver`, cmd),
  relatorio: (filtros: { inicio?: string; fim?: string } = {}) =>
    httpPdv.get<RelatorioPdv>(`/api/relatorios/pdv${querystring(filtros)}`),

  // Fora da trava: o PIN é cadastro do colaborador, não operação do PDV.
  definirPin: (colaboradorId: string, pin: string | null) =>
    httpClient.post<void>(`/api/colaboradores/${colaboradorId}/pin`, { pin }),
};
